import { useCallback, useState } from 'react';
import { useChat } from '../../../context/chat/ChatContext';
import { fetchGraphQL } from '../gql.client';
import { changePlanMutation } from '../gql.mutations';
import { getPlansQuery } from '../gql.queries';

import type { Plan, UsePlansHook, UsePlansHookLoadings } from '../../../types/Plan.type';

export const usePlans = (): UsePlansHook => {
    const { user, plan, setSelectedPlan } = useChat();
    const [error, setError] = useState<any>(null);
    const [planState, setPlanState] = useState<UsePlansHookLoadings>({
        plans: false,
        updatePlan: {
            loading: false,
            success: null,
            error: null,
        },
    });

    const [currentPlan, setCurrentPlan] = useState<number | null>(plan?.id ?? null);
    const [plans, setPlans] = useState<Plan[]>([]);

    const getPlans = useCallback(async () => {
        setPlanState(prev => ({ ...prev, plans: true }));
        setError(null);


        try {
            const result = await fetchGraphQL({
                url: `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/graphql/user`,
                query: getPlansQuery(),
                variables: {
                    zipcode: user?.addresses?.[0]?.postcode as string,
                },
            });

            if (result.error) {
                setError(result.error);
            } else {
                const availablePlans = result?.data?.availablePlans || [];
                setPlans(availablePlans);
                const selectedPlan = availablePlans.find(
                    (availablePlan: Plan) => availablePlan.id === plan?.id
                );

                if (selectedPlan) {
                    setCurrentPlan(selectedPlan.id);
                }
            }
        } catch (err) {
            setError(err);
        } finally {
            setPlanState(prev => ({ ...prev, plans: false }));
        }
    }, [user, plan?.id]);

    const updatePlan = useCallback(async (planId: number) => {
        if (user === null) return;

        setPlanState(prev => ({
            ...prev,
            updatePlan: {
                error: null,
                loading: true,
                success: null,
                data: prev?.updatePlan?.data ?? null
            }
        }));

        const selectedDeliveryDays = user.deliveryDays;

        try {
            const resp = await fetchGraphQL({
                url: `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/graphql/user`,
                query: changePlanMutation(),
                variables: {
                    planChange: {
                        plan: planId,
                        deliveryDays: {
                            days: selectedDeliveryDays.map((d: any) => d.day),
                            start: selectedDeliveryDays[0].time_start || '',
                            end: selectedDeliveryDays[0].time_end || ''
                        },
                        origin: 'Subscription web ai-waiter',
                    },
                },
            });

            if ((resp as any)?.data?.changePlan) {
                setCurrentPlan(planId);
                console.log('test', plans, planId)
                console.log('test', plans.find(p => p.id === planId))
                setSelectedPlan(plans.find(p => p.id === planId) as Plan);
                setPlanState(prev => ({
                    ...prev,
                    updatePlan: {
                        error: false,
                        loading: false,
                        success: true,
                        data: (resp as any)?.data?.changePlan ?? null
                    }
                }));
            }
        } catch (err) {
            setError(err);
            setPlanState(prev => ({
                ...prev,
                updatePlan: {
                    error: null,
                    loading: false,
                    success: false,
                    data: prev?.updatePlan?.data ?? null
                }
            }));
        }
    }, [plans, user, setSelectedPlan]);

    return {
        plans,
        error,
        planState,
        currentPlan,
        getPlans,
        updatePlan,
    };
};